import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import {
  getDirectPartnersBySearchAction,
  getPartnersBySearchAction,
  saveStepData,
} from "../../../Redux/AddOrEditPartner/AddOrEditPartnerActions";
import UplinerPartnerField from "./step6/UplinerPartnerField";
import DirectPartnerField from "./step6/DirectPartnerField";
import PasswordFields from "./step6/PasswordFields";
import {
  STEP_SIX_INITIAL_VALUES,
  STEP_SIX_SCHEMA,
  debouncedSearchHandler,
} from "./step6/utils";
import TermsField from "./step6/TermsField";
import { absoluteUrl } from "../../../_helper";
import { genericResetStep } from "../../../Redux/AddOrEditPartner/AddOrEditPartnerSlice";
import RowLoader from "../../../Components/UI/RowLoader";
import HeaderStep from "./HeaderStep";

const PartnerStepSix = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { step1, isLoading } = useSelector((state) => state?.AddOrEditPartner);
  const { stepInfo: Step1Info } = step1;
  const [errorList, setErrorList] = useState({});
  const [uplinerError, setUplinerError] = useState(false);
  const [directPartnerError, setDirectPartnerError] = useState(false);
  const onSubmitSuccessFuly = () => {
    Swal.fire({
      title: "Good!",
      text: "Partner was added successfully",
      icon: "success",
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/partners-list");
      }
    });
  };

  const handleSubmit = async (values, { setErrors }) => {
    if (uplinerError || directPartnerError) return;
    const ObjectToSubmit = {
      client_id: Step1Info?.id,
      direct_partner_id: +values?.direct_partner_id,
      password: values?.password,
      password_confirmation: values?.password_confirmation,
    };
    if (values?.upliner_partner_id) {
      ObjectToSubmit.upliner_partner_id = values?.upliner_partner_id;
    }
    if (values?.direction) {
      ObjectToSubmit.direction = values?.direction;
    }

    try {
      await dispatch(
        saveStepData(
          { ...ObjectToSubmit },
          6,
          false,
          onSubmitSuccessFuly,
          Step1Info?.client?.client_category_id
        )
      );
    } catch (error) {
      const errorData = error?.response?.data?.message || error?.response?.data;
      const formErrors = {};

      if (typeof errorData === "string") {
        formErrors.general = errorData; // Assign the string error message to a general key
      } else if (Array.isArray(errorData)) {
        formErrors.general = errorData.join(", "); // Join array elements into a single string
      } else {
        for (const key in errorData) {
          if (errorData.hasOwnProperty(key)) {
            formErrors[key] = errorData[key][0]; // assuming the error messages are arrays
          }
        }
      }

      setErrorList(formErrors);
    }
  };

  useEffect(() => {
    dispatch(genericResetStep(6));
  }, [dispatch]);

  return (
    <>
      <HeaderStep
        first_name={Step1Info?.first_name}
        second_name={Step1Info?.second_name}
        last_name={Step1Info?.last_name}
        stepName={"Activate Partner"}
        stepNumber={"6"}
      />
      <div className="tw-w-full tw-justify-end tw-flex tw-gap-8 tw-px-5">
        {Object.keys(errorList).length > 0 && (
          <div className="alert alert-danger tw-w-full">
            {Object.values(errorList).map((item, index) => (
              <div key={index}>
                <p className="!tw-mb-0">{item}</p>
              </div>
            ))}
          </div>
        )}
        <Link to={`/tree`} target="_blank" className="tw-inline-block">
          <img
            className="icon-color"
            src={absoluteUrl("imgs/tree.png")}
            style={{ width: 40 }}
            alt="tree"
          />
        </Link>
      </div>
      <div className="card-body tw-relative !tw-pt-0">
        <Formik
          initialValues={{
            ...STEP_SIX_INITIAL_VALUES,
            clientId: Step1Info?.client?.client_category_id,
          }}
          validationSchema={STEP_SIX_SCHEMA}
          onSubmit={handleSubmit}
        >
          {({
            handleChange,
            handleBlur,
            values,
            setFieldValue,
            touched,
            errors,
            setFieldError,
            isValid,
            setFieldTouched,
          }) => {
            return (
              <Form className="d-flex flex-column">
                {errors.submit && (
                  <div className="alert alert-danger">{errors.submit}</div>
                )}
                {(Step1Info?.client?.client_category_id == 2 ||
                  Step1Info?.client?.client_category_id == 3) && (
                  <UplinerPartnerField
                    uplinerError={uplinerError}
                    setUplinerError={setUplinerError}
                    values={values}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    debouncedAsyncValidation={(keyName, keyValue) =>
                      debouncedSearchHandler(
                        dispatch,
                        getPartnersBySearchAction,
                        keyName,
                        keyValue
                      )
                    }
                    touched={touched}
                    errors={errors}
                  />
                )}
                <DirectPartnerField
                  directPartnerError={directPartnerError}
                  setDirectPartnerError={setDirectPartnerError}
                  clientId={Step1Info?.client?.client_category_id}
                  values={values}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  debouncedDirectAsyncValidation={(
                    keyName,
                    keyValue,
                    uplinerId
                  ) => {
                    if (Step1Info?.client?.client_category_id == 1) {
                      debouncedSearchHandler(
                        dispatch,
                        getPartnersBySearchAction,
                        keyName,
                        keyValue,
                        uplinerId,
                        1
                      );
                    } else {
                      debouncedSearchHandler(
                        dispatch,
                        getDirectPartnersBySearchAction,
                        keyName,
                        keyValue,
                        uplinerId
                      );
                    }
                  }}
                  touched={touched}
                  errors={errors}
                />
                <PasswordFields touched={touched} errors={errors} />
                <TermsField
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  values={values}
                />
                <button
                  disabled={!isValid || uplinerError || directPartnerError}
                  type="submit"
                  className="btn btn-primary ml-auto mt-5 !tw-w-[100px]"
                >
                  {isLoading ? <RowLoader paddingStyle="tw-py-0" /> : "Submit"}
                </button>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default PartnerStepSix;
